// js/Component/AddressBook/AddressBookView.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
    fetchAddressBooksForDevice,
    fetchAddressBookEntries,
    deleteAddressBookEntry,
    fetchDeviceImage,
    fetchDeviceSettings,
    updateDeviceSetting,
} from "js/Api/index";

import AddressBookEntryRow from "./AddressBookEntryRow";
import AddContactModal from "./AddContactModal";
import EditContactModal from "./EditContactModal";
import ContactsReorderModal from "js/Component/ContactsReorderModal";

const ADDRESS_BOOK_ORDER_KEY = "settings.device_address_book_order";
const MAX_PER_PAGE_KEY = "settings.max.contacts_per_page";

const POLL_INTERVAL_MS = 30000;

// Server timestamps come back without a timezone marker (e.g. "2026-04-29T04:48:41.247366").
// They're UTC, so append "Z" to make them valid ISO-8601 UTC for the `updated_since` query param.
const toUtcZ = (iso) => {
    if (!iso) return null;
    if (iso.endsWith("Z")) return iso;
    if (/[+-]\d{2}:?\d{2}$/.test(iso)) return new Date(iso).toISOString();
    return `${iso}Z`;
};

function AddressBookView({
    deviceId,
    canEdit = true,
    verbose = false,
    searchQuery = "",
}) {
    const [loading, setLoading] = useState(false);
    const [book, setBook] = useState(null);
    const [entries, setEntries] = useState([]);

    const [showAddModal, setShowAddModal] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [deletingEntry, setDeletingEntry] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const lastSyncRef = useRef(null);
    const [deviceImages, setDeviceImages] = useState({});

    const [showReorderModal, setShowReorderModal] = useState(false);
    const [orderSetting, setOrderSetting] = useState(null);
    const [orderList, setOrderList] = useState([]);
    const [maxContacts, setMaxContacts] = useState(8);
    const [savingOrder, setSavingOrder] = useState(false);

    const filteredEntries = (() => {
        const q = (searchQuery || "").trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((e) => {
            const name = (e.displayName || "").toLowerCase();
            const phone = (e.phoneNumberRaw || "").toLowerCase();
            const target = (e.targetDeviceId || "").toLowerCase();
            return name.includes(q) || phone.includes(q) || target.includes(q);
        });
    })();

    const loadAll = async () => {
        if (!deviceId) return;
        setLoading(true);
        try {
            const booksRes = await fetchAddressBooksForDevice(deviceId);
            if (!booksRes.success) {
                toast.error(booksRes.error || "Failed to load address books");
                setBook(null);
                setEntries([]);
                return;
            }
            const books = Array.isArray(booksRes.data) ? booksRes.data : [];
            const defaultBook =
                books.find((b) => b.systemKey === "default") || books[0] || null;

            setBook(defaultBook);

            if (!defaultBook) {
                setEntries([]);
                return;
            }

            const entriesRes = await fetchAddressBookEntries(defaultBook.id);
            if (entriesRes.success) {
                const list = Array.isArray(entriesRes.data) ? entriesRes.data : [];
                setEntries(list.filter((e) => e.isActive !== false));
                const maxUpdatedAt = list.reduce(
                    (max, e) =>
                        e.updatedAt && (!max || e.updatedAt > max) ? e.updatedAt : max,
                    null
                );
                lastSyncRef.current = toUtcZ(maxUpdatedAt) || new Date().toISOString();
            } else {
                toast.error(entriesRes.error || "Failed to load contacts");
                setEntries([]);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    useEffect(() => {
        if (!book?.id) return undefined;

        const pollIncremental = async () => {
            const since = lastSyncRef.current;
            if (!since) return;
            const res = await fetchAddressBookEntries(book.id, {
                updatedSince: since,
                includeDeleted: false,
            });
            if (!res.success) {
                console.warn("Address book incremental sync failed:", res.error);
                return;
            }
            const changes = Array.isArray(res.data) ? res.data : [];
            if (changes.length > 0) {
                setEntries((prev) => {
                    const byId = new Map(prev.map((e) => [e.id, e]));
                    for (const c of changes) {
                        if (c.isActive === false) {
                            byId.delete(c.id);
                        } else {
                            byId.set(c.id, c);
                        }
                    }
                    return Array.from(byId.values());
                });
                const maxUpdatedAt = changes.reduce(
                    (max, c) =>
                        c.updatedAt && (!max || c.updatedAt > max) ? c.updatedAt : max,
                    null
                );
                const normalized = toUtcZ(maxUpdatedAt);
                if (normalized) {
                    lastSyncRef.current = normalized;
                }
            }
            // No changes → leave cursor unchanged so the next poll asks the same window again.
        };

        const interval = setInterval(pollIncremental, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [book?.id]);

    // Fetch profile pictures for video_contact entries via the device profile API.
    // Skips device IDs already in the cache; runs again when entries change so new
    // contacts (added live or via incremental sync) get fetched too.
    useEffect(() => {
        const missing = [];
        for (const e of entries) {
            if (
                e.entryType === "video_contact" &&
                e.targetDeviceId &&
                !(e.targetDeviceId in deviceImages)
            ) {
                missing.push(e.targetDeviceId);
            }
        }
        if (missing.length === 0) return undefined;

        let cancelled = false;
        (async () => {
            const updates = {};
            await Promise.all(
                [...new Set(missing)].map(async (id) => {
                    const res = await fetchDeviceImage(id);
                    updates[id] = res.success ? (res.data?.profilePicture || null) : null;
                })
            );
            if (!cancelled && Object.keys(updates).length > 0) {
                setDeviceImages((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries]);

    const handleCreated = (created) => {
        if (created) {
            setEntries((prev) => [created, ...prev]);
        } else {
            loadAll();
        }
    };

    const handleUpdated = (updated) => {
        if (updated) {
            setEntries((prev) =>
                prev.map((e) => (e.id === updated.id ? updated : e))
            );
        } else {
            loadAll();
        }
    };

    const handleConfirmDelete = async () => {
        if (!deletingEntry) return;
        setDeleting(true);
        const res = await deleteAddressBookEntry(deletingEntry.id);
        setDeleting(false);
        if (res.success) {
            setEntries((prev) => prev.filter((e) => e.id !== deletingEntry.id));
            toast.success(res.message || "Contact deleted");
            setDeletingEntry(null);
        } else {
            toast.error(res.error || res.message || "Failed to delete contact");
        }
    };

    const openReorder = async () => {
        if (!deviceId) return;
        const res = await fetchDeviceSettings(deviceId);
        if (!res.success) {
            toast.error(res.error || "Failed to load device settings");
            return;
        }
        const settings = Array.isArray(res.data) ? res.data : [];
        const setting = settings.find((s) => s.friendlyName === ADDRESS_BOOK_ORDER_KEY);
        const maxSetting = settings.find((s) => s.friendlyName === MAX_PER_PAGE_KEY);
        if (!setting) {
            toast.error("Address book order setting not found on this device");
            return;
        }

        let savedIds = [];
        try {
            const parsed = JSON.parse(setting.value || "[]");
            if (Array.isArray(parsed)) savedIds = parsed.map(String);
        } catch (err) {
            console.warn("Failed to parse address book order:", err);
        }

        // Drop ids no longer in entries, then append any new entries
        const liveIds = new Set(entries.map((e) => String(e.id)));
        const filtered = savedIds.filter((id) => liveIds.has(id));
        entries.forEach((e) => {
            if (!filtered.includes(String(e.id))) filtered.push(String(e.id));
        });

        setOrderSetting(setting);
        setMaxContacts(parseInt(maxSetting?.value, 10) || 8);
        setOrderList(filtered);
        setShowReorderModal(true);
    };

    const handleSaveAddressBookOrder = async (orderedIds) => {
        if (!orderSetting || !deviceId) return;
        try {
            setSavingOrder(true);
            const valueStr = JSON.stringify(orderedIds || []);
            const result = await updateDeviceSetting(orderSetting, deviceId, valueStr);
            if (result.success) {
                setOrderList(orderedIds);
                toast.success("Address book order updated");
                setShowReorderModal(false);
            } else {
                toast.error(result.error || "Failed to update order");
            }
        } catch (err) {
            console.error("Error saving address book order:", err);
            toast.error("Failed to update order");
        } finally {
            setSavingOrder(false);
        }
    };

    const reorderItems = useMemo(() => {
        const entryById = {};
        entries.forEach((e) => {
            entryById[String(e.id)] = e;
        });
        return orderList.map((id) => {
            const e = entryById[id];
            if (!e) return { id, label: id, avatarSrc: null };
            const isVideo = e.entryType === "video_contact";
            let avatarSrc = null;
            if (isVideo) {
                const base64 = deviceImages[e.targetDeviceId];
                if (base64) avatarSrc = `data:image/png;base64,${base64}`;
            } else if (e.avatarUrl) {
                avatarSrc = `${e.avatarUrl}${e.avatarUpdatedAt
                    ? `?v=${encodeURIComponent(e.avatarUpdatedAt)}`
                    : ""}`;
            }
            return {
                id,
                label: e.displayName || String(id),
                avatarSrc,
            };
        });
    }, [orderList, entries, deviceImages]);

    if (!deviceId) {
        return (
            <div style={{ padding: 20 }}>
                <p>No device selected.</p>
            </div>
        );
    }

    return (
        <div style={{ paddingTop: 10 }}>
            <div
                style={{
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    borderRadius: 6,
                    padding: "10px 14px",
                    marginBottom: 16,
                    fontSize: 13,
                    color: "#7c2d12",
                }}
            >
                These are the device's <strong>Home Page Contacts</strong> — the
                favourites shown on the device home screen that the user taps once
                to call. Keep this list short and ordered by priority.
            </div>

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                }}
            >
                <span style={{ fontWeight: 600 }}>
                    Home Page Contacts{book ? ` — ${book.name}` : ""}
                </span>
                {canEdit && book && (
                    <div style={{ display: "flex", gap: 8 }}>
                        {entries.length >= 2 && (
                            <button
                                type="button"
                                className="btn cur-p btn-outline-dark"
                                onClick={openReorder}
                            >
                                Reorder Contacts
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn cur-p btn-primary"
                            onClick={() => setShowAddModal(true)}
                        >
                            + Add Contact
                        </button>
                    </div>
                )}
            </div>

            {loading ? (
                <div style={{ padding: "20px 0", textAlign: "center" }}>
                    <i
                        className="fa fa-spinner fa-spin"
                        style={{ fontSize: 24, marginRight: 8, color: "#007bff" }}
                    />
                    <p style={{ marginTop: 10, color: "#666" }}>Loading contacts...</p>
                </div>
            ) : !book ? (
                <p style={{ color: "#777" }}>
                    No address book found for this device.
                </p>
            ) : entries.length === 0 ? (
                <div
                    style={{
                        textAlign: "center",
                        padding: 30,
                        border: "1px dashed #ddd",
                        borderRadius: 6,
                        color: "#666",
                    }}
                >
                    <p style={{ marginBottom: canEdit ? 10 : 0 }}>No contacts yet.</p>
                    {canEdit && (
                        <button
                            type="button"
                            className="btn cur-p btn-primary"
                            onClick={() => setShowAddModal(true)}
                        >
                            + Add one
                        </button>
                    )}
                </div>
            ) : filteredEntries.length === 0 ? (
                <div
                    style={{
                        textAlign: "center",
                        padding: 30,
                        border: "1px dashed #ddd",
                        borderRadius: 6,
                        color: "#666",
                    }}
                >
                    <p style={{ margin: 0 }}>
                        No contacts match "{searchQuery.trim()}".
                    </p>
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Contact</th>
                                <th>Type</th>
                                <th>Phone</th>
                                {verbose && <th>Target Device</th>}
                                {verbose && <th>Source</th>}
                                {verbose && <th>Updated</th>}
                                {verbose && <th>Entry ID</th>}
                                {canEdit && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEntries.map((entry) => (
                                <AddressBookEntryRow
                                    key={entry.id}
                                    entry={entry}
                                    verbose={verbose}
                                    deviceImageBase64={
                                        entry.entryType === "video_contact" && entry.targetDeviceId
                                            ? deviceImages[entry.targetDeviceId]
                                            : null
                                    }
                                    onEdit={(e) => setEditingEntry(e)}
                                    onDelete={(e) => setDeletingEntry(e)}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showAddModal && book && (
                <AddContactModal
                    addressBookId={book.id}
                    onClose={() => setShowAddModal(false)}
                    onCreated={handleCreated}
                />
            )}

            {editingEntry && (
                <EditContactModal
                    entry={editingEntry}
                    onClose={() => setEditingEntry(null)}
                    onUpdated={handleUpdated}
                />
            )}

            {deletingEntry && (
                <div className="modal" role="dialog" style={{ display: "block" }}>
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: 400 }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Delete Contact</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setDeletingEntry(null)}
                                    disabled={deleting}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to delete "
                                    {deletingEntry.displayName}"?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={() => setDeletingEntry(null)}
                                    disabled={deleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-danger"
                                    onClick={handleConfirmDelete}
                                    disabled={deleting}
                                >
                                    {deleting ? "Deleting..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ContactsReorderModal
                open={showReorderModal}
                items={reorderItems}
                maxPerPage={maxContacts}
                saving={savingOrder}
                onClose={() => setShowReorderModal(false)}
                onSave={handleSaveAddressBookOrder}
            />
        </div>
    );
}

export default AddressBookView;
